var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ScriptComposeFacet, PendingMessages, UTILITY_ROOM_UID, ROOT_MODEL_UID } from "flavr-common";
import { ScriptsCreationFinished } from "@messages/scripting/ScriptMessages";
import { modelPool, Model, isNonSessionRoom, RoomModel } from "@modules/model";
import { ScriptManager, SCRIPT_FUNCTION_NAMES_SET } from "./ScriptManager";
import { ModelScript } from "../model/ModelScript";
import { Room } from "../Room";
import { ModelScriptFunctions } from "../model/ModelScriptFunctions";
const MAX_SYNCHRONOUS_FLUSH_ROUNDS = 100;
export class ScriptContext {
    constructor(connection) {
        this.connection = connection;
        this.models = new Map();
        this.scripts = new Map();
        this.scriptFuncHashToName = new Map();
        this.scriptManager = new ScriptManager();
        this.scriptUpdateQueues = new Map();
        this.lastScriptIdx = 0;
        this.rooms = new Map();
        this.utilityRoom = new Room(this, UTILITY_ROOM_UID);
        this.currentRoomModelUID = ROOT_MODEL_UID;
        this.isRoomHost = false;
        this.pendingMessages = new PendingMessages();
        this.isFlushingMessages = false;
        this.isDeferredFlushScheduled = false;
        this.rooms.set(UTILITY_ROOM_UID, this.utilityRoom);
    }
    generateNextScriptIdx() {
        return this.lastScriptIdx++;
    }
    get currentScript() {
        if (this.currentScriptIdx === undefined)
            return undefined;
        return this.scripts.get(this.currentScriptIdx);
    }
    hasCurrentRoom() {
        return this.currentRoom !== undefined;
    }
    wrapWeakScript(script) {
        if (script.scriptInstance === undefined)
            return undefined;
        // const isGetter = ( x, name ) => ( Object.getOwnPropertyDescriptor( x, name ) || {} ).get
        // const isFunction = (x, name ) => typeof x[ name ] === "function";
        // const funcNames = Object.getOwnPropertyNames(Object.getPrototypeOf(script));
        const prototype = Reflect.getPrototypeOf(script.scriptInstance);
        if (prototype == null)
            return undefined;
        const funcNames = Reflect.ownKeys(prototype);
        const obj = {
            modelId: script.modelUID, // ToDo: Can we rename this to modelUID or will this break the code?
            scriptName: script.scriptName,
            scriptIdx: script.scriptIdx
        };
        for (const funcName of funcNames) {
            if (funcName === "constructor")
                continue;
            const func = script.scriptInstance[funcName];
            if (typeof func !== "function")
                continue;
            if (SCRIPT_FUNCTION_NAMES_SET.has(funcName))
                continue;
            obj[funcName] = (...params) => {
                if (this.currentRoom === undefined)
                    return false;
                const script = this.scripts.get(obj.scriptIdx);
                if (script === undefined)
                    return false;
                if (script.scriptInstance === undefined)
                    return false;
                return script.scriptInstance[funcName](...params);
            };
        }
        const modelScriptFunctions = new ModelScriptFunctions(this, script.modelUID, obj, script.room, script.scriptId, script.scriptIdx);
        void modelScriptFunctions;
        return obj;
    }
    updateModelScripts(uid, scriptInfos, timestamp) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const previousUpdate = (_a = this.scriptUpdateQueues.get(uid)) !== null && _a !== void 0 ? _a : Promise.resolve();
            const currentUpdate = previousUpdate
                .catch(() => { })
                .then(() => __awaiter(this, void 0, void 0, function* () {
                yield this.updateModelScriptsInternal(uid, scriptInfos, timestamp);
            }));
            this.scriptUpdateQueues.set(uid, currentUpdate);
            try {
                yield currentUpdate;
            }
            finally {
                if (this.scriptUpdateQueues.get(uid) === currentUpdate) {
                    this.scriptUpdateQueues.delete(uid);
                }
            }
        });
    }
    updateModelScriptsInternal(uid, scriptInfos, timestamp) {
        return __awaiter(this, void 0, void 0, function* () {
            let scriptModelData = this.models.get(uid);
            if (scriptModelData === undefined) {
                this.models.set(uid, (scriptModelData = {
                    createdTime: timestamp,
                    scripts: new Map()
                }));
            }
            else {
                if (scriptModelData.createdTime > timestamp)
                    return;
                scriptModelData.createdTime = timestamp;
                for (const script of scriptModelData.scripts.values()) {
                    if (scriptInfos.find((element) => element.scriptId === script.scriptId) == null) {
                        this.destroyScript(script);
                    }
                }
            }
            for (const scriptInfo of scriptInfos) {
                let script = scriptModelData.scripts.get(scriptInfo.scriptId);
                if (script === undefined) {
                    const model = modelPool.getModel(uid);
                    if (model === undefined)
                        return;
                    script = yield this.createScript(model, scriptInfo, timestamp);
                }
                else {
                    if (scriptModelData.createdTime > timestamp)
                        return;
                    script.syncExecutionState(scriptInfo.autoExecute, scriptInfo.active);
                    script.updateProperties(scriptInfo);
                }
            }
        });
    }
    createScript(model, scriptInfo, timestamp) {
        return __awaiter(this, void 0, void 0, function* () {
            const roomModel = model.getRoom();
            if (roomModel === undefined)
                throw new Error(`No parent room for model ${model.getName()}`);
            const { room, wasRoomCreated } = this.getOrCreateRoom(roomModel);
            const script = new ModelScript(model.getUID(), scriptInfo.scriptId, this, room, this.generateNextScriptIdx());
            script.syncExecutionState(scriptInfo.autoExecute, scriptInfo.active);
            room.addScript(script);
            let scriptModelData = this.models.get(model.getUID());
            if (scriptModelData === undefined) {
                this.models.set(model.getUID(), (scriptModelData = {
                    createdTime: timestamp,
                    scripts: new Map()
                }));
            }
            scriptModelData.scripts.set(scriptInfo.scriptId, script);
            yield script.scriptChanged(scriptInfo);
            if (wasRoomCreated) {
                if (this.currentRoomModelUID === roomModel.getUID()) {
                    this.onChangeRoom(roomModel.getUID());
                }
            }
            return script;
        });
    }
    destroyScripts(uid, timestamp) {
        const model = this.models.get(uid);
        if (model === undefined)
            return;
        if (model.createdTime > timestamp) {
            return;
        }
        this.models.delete(uid);
        for (const script of model.scripts.values()) {
            this.destroyScript(script);
        }
    }
    destroyScript(script) {
        var _a;
        this.scripts.delete(script.scriptIdx);
        const scriptModelData = this.models.get(script.modelUID);
        if (scriptModelData !== undefined) {
            scriptModelData.scripts.delete(script.scriptId);
            if (scriptModelData.scripts.size === 0) {
                this.models.delete(script.modelUID);
            }
        }
        const room = (_a = this.rooms.get(script.room.roomUID)) !== null && _a !== void 0 ? _a : script.room;
        if (room !== undefined) {
            room.removeScript(script);
            if (this.currentRoom === room) {
                script.onLeaveRoom();
            }
        }
        script.destroy();
    }
    scriptChanged(scriptID) {
        var _a;
        this.scriptManager.scriptChanged(scriptID);
        for (const model of this.models.values()) {
            for (const script of model.scripts.values()) {
                if (script.scriptId !== scriptID)
                    continue;
                const model = modelPool.getModel(script.modelUID);
                const facet = (_a = model === null || model === void 0 ? void 0 : model.getFacet(ScriptComposeFacet)) === null || _a === void 0 ? void 0 : _a.clone();
                let scriptInfo;
                if (facet !== undefined) {
                    for (const info of facet.scripts) {
                        if (info.scriptId === scriptID) {
                            scriptInfo = info;
                        }
                    }
                }
                void script.scriptChanged(scriptInfo);
            }
        }
    }
    getModelInCurrentRoom(uid) {
        if (this.currentRoom === undefined) {
            return this.utilityRoom.models.get(uid);
        }
        const model = this.currentRoom.models.get(uid);
        if (model !== undefined)
            return model;
        return this.utilityRoom.models.get(uid);
    }
    getScriptInCurrentRoom(modelUID, scriptId) {
        const model = this.getModelInCurrentRoom(modelUID);
        if (model === undefined)
            return undefined;
        return model.scripts.get(scriptId);
    }
    onChangeRoom(roomUid) {
        if (this.currentRoomModelUID === roomUid)
            return;
        const newRoomModel = modelPool.getModel(roomUid);
        if (newRoomModel === undefined)
            return;
        this.currentRoomModelUID = newRoomModel.getUID();
        this.utilityRoom.onLeaveRoom();
        if (this.currentRoom !== undefined) {
            this.currentRoom.onLeaveRoom();
        }
        const { room } = this.getOrCreateRoom(roomUid);
        this.currentRoom = room;
        if (this.currentRoom !== undefined) {
            this.currentRoom.onEnterRoom();
            this.currentRoom.init();
        }
        this.utilityRoom.onEnterRoom();
        this.utilityRoom.init();
    }
    onLayoutCreationFinished(roomUID, isFirstCreation) {
        this.utilityRoom.onLayoutCreationFinished(isFirstCreation);
        const room = this.rooms.get(roomUID);
        if (room === undefined) {
            // we have no scripts for this room
            this.pendingMessages.push(new ScriptsCreationFinished(roomUID));
            this.flushMessages();
            return;
        }
        room.onLayoutCreationFinished(isFirstCreation);
    }
    initializeRoomScripts() {
        this.utilityRoom.init();
        if (this.currentRoom === undefined)
            return;
        this.currentRoom.init();
    }
    getScriptName(uid) {
        const model = this.getModelInCurrentRoom(uid);
        if (model === undefined)
            return undefined;
        const script = model.scripts.values().next().value;
        return script === null || script === void 0 ? void 0 : script.scriptName;
    }
    *getModelsInCurrentRoomIterator() {
        for (const model of this.utilityRoom.models.values()) {
            yield model;
        }
        if (this.currentRoom !== undefined) {
            for (const model of this.currentRoom.models.values()) {
                yield model;
            }
        }
    }
    flushMessages() {
        // Local delivery is synchronous and may trigger script callbacks that flush again (e.g. transform updates).
        // Nested calls only enqueue; the outermost call drains the queue iteratively.
        if (this.isFlushingMessages)
            return;
        if (this.pendingMessages.isEmpty())
            return;
        this.isFlushingMessages = true;
        try {
            for (let round = 0; this.pendingMessages.hasMessages(); round++) {
                if (round >= MAX_SYNCHRONOUS_FLUSH_ROUNDS) {
                    if (!this.isDeferredFlushScheduled) {
                        console.error("[scripting] Script messages keep triggering new script messages; deferring the remaining messages");
                        this.isDeferredFlushScheduled = true;
                        setTimeout(() => {
                            this.isDeferredFlushScheduled = false;
                            this.flushMessages();
                        }, 0);
                    }
                    return;
                }
                const messages = this.pendingMessages.get();
                this.pendingMessages.clear();
                this.connection.sendMultiple(messages);
            }
        }
        finally {
            this.isFlushingMessages = false;
        }
    }
    getOrCreateRoom(roomModelOrUID) {
        const roomModel = (roomModelOrUID instanceof Model) ? roomModelOrUID : modelPool.getModel(roomModelOrUID);
        if (roomModel === undefined) {
            throw new Error("Couldn't get or create a room because the room model doesn't exist");
        }
        let room = this.rooms.get(roomModel.getUID());
        let isRoomCreated = false;
        if (room === undefined) {
            isRoomCreated = true;
            let primaryRoom = modelPool.getPrimaryRoom();
            if (primaryRoom === undefined) {
                primaryRoom = roomModel instanceof RoomModel ? roomModel : roomModel.isRoom() ? roomModel : undefined;
                while (!(primaryRoom === null || primaryRoom === void 0 ? void 0 : primaryRoom.getRoomOrThrow().isRoot())) {
                    primaryRoom = primaryRoom === null || primaryRoom === void 0 ? void 0 : primaryRoom.getRoomOrThrow();
                }
            }
            // If model room is not within rest, then we use the utility room instead
            if (isNonSessionRoom(primaryRoom) || !primaryRoom.isAncestorOfOrEquals(roomModel)) {
                isRoomCreated = false;
                room = this.utilityRoom;
                room.onEnterRoom();
                room.init();
            }
            else {
                room = new Room(this, roomModel.getUID());
            }
            this.rooms.set(roomModel.getUID(), room);
        }
        return { room, wasRoomCreated: isRoomCreated };
    }
}
